import { useState, useCallback } from 'react';
import type { Game } from './useGames';
import type { GameLive } from './useGamesLive';
import type { User as GameUser } from '../types/user';
import { fetchLaunchGame } from '../lib/functions';
import { postMessageHandler, POST_MESSAGE_ACTIONS } from '../utils/postMessageHandler';
import type { AuthToken } from '../types/user';
import log from '../utils/logger';
import logger from "../utils/logger";

interface GameLaunchState {
  isGameLoading: boolean;
  isGameIframeOpen: boolean;
  gameUrl: string | null;
  currentGameName: string;
  lobbyUrl: string | null;
}

interface UseGameLaunchReturn extends GameLaunchState {
  handleGamePlay: (game: Game) => Promise<void>;
  handleGameIframeClose: () => void;
  createLoginParamsString: (additionalParams?: Record<string, any>) => string;
}

export function useGameLaunch(
  gameUser: GameUser | null,
  updateUserToken: (tokenData: AuthToken) => void,
  logout: () => void,
  selectedMenuId: string | null,
  getCurrentGameType: () => string,
  findLiveGameById: (gameId: string) => GameLive | undefined,
  showError: (title: string, message?: string, duration?: number) => void,
  showWarning: (title: string, message: string) => void
): UseGameLaunchReturn {
  const [isGameLoading, setIsGameLoading] = useState(false);
  const [isGameIframeOpen, setIsGameIframeOpen] = useState(false);
  const [gameUrl, setGameUrl] = useState<string | null>(null);
  const [currentGameName, setCurrentGameName] = useState<string>('');
  const [lobbyUrl, setLobbyUrl] = useState<string | null>(null);

  const createLoginParamsString = useCallback((additionalParams?: Record<string, any>) => {
    const baseParams = {
      tab: getCurrentGameType(),
      menu: selectedMenuId,
      ...additionalParams
    };
    
    return Object.entries(baseParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
  }, [selectedMenuId, getCurrentGameType]);

  const handleGamePlay = useCallback(async (game: Game) => {
    log.debug('🎮 Gioco cliccato:', game);
    
    if (!gameUser || !gameUser.isLogged) {
      let gameParams = {
        platformID: game.platform.id,
        launchCode: game.launchCode,
        partnerID: game.partnerID,
        gameType: game.type,
        gameName: game.name
      };
      
      if (game.type === 'casinolive') {
        const originalLiveGame = findLiveGameById(game.id);
        if (originalLiveGame?.tableConfig) {
          let launchCode = originalLiveGame.tableConfig.imsGameType || '';
          if (launchCode && launchCode.length > 0) {
            launchCode = launchCode + ';';
          }
          launchCode = launchCode + (originalLiveGame.tableConfig.launchAlias?.[0] || '');
          
          gameParams = {
            ...gameParams,
            launchCode: launchCode,
            platformID: originalLiveGame.platform?.id || game.platform.id,
            partnerID: originalLiveGame.partnerID || game.partnerID
          };
        }
      }

      logger.debug("gameParams:", gameParams);
      const paramsString = createLoginParamsString(gameParams);
      postMessageHandler.sendToParent(POST_MESSAGE_ACTIONS.LOGIN_FOR_CASINO, { params: paramsString });
      return;
    }
    
    try {
      if (!gameUser || !gameUser.isLogged) {
        showError('Sessione scaduta', 'Effettua nuovamente l\'accesso', 3000);
        return;
      }

      setIsGameLoading(true);

      let gameToLaunch = { ...game };
      
      if (game.type === 'casinolive') {
        const originalLiveGame = findLiveGameById(game.id);
        
        if (originalLiveGame?.tableConfig) {
          let launchCode = originalLiveGame.tableConfig.imsGameType || '';
          if (launchCode && launchCode.length > 0) {
            launchCode = launchCode + ';';
          }
          launchCode = launchCode + (originalLiveGame.tableConfig.launchAlias?.[0] || '');
          
          gameToLaunch = {
            ...gameToLaunch,
            launchCode: launchCode,
            platform: originalLiveGame.platform,
            partnerID: originalLiveGame.partnerID || gameToLaunch.partnerID
          };
        }
      }

      const launchResult = await fetchLaunchGame(gameToLaunch, gameUser);

      if (launchResult && launchResult.clearLoginParams) {
        setIsGameLoading(false);
        showError('Sessione scaduta', undefined, 3000);
        setTimeout(() => {
          logout();
        }, 100);
        return;
      }

      // Gestione del token dalla risposta
      if (launchResult && launchResult.token) {
        updateUserToken(launchResult.token);
      }

      if (launchResult && launchResult.launchURL && launchResult.launchURL.real) {
        const gameUrlToLoad = launchResult.launchURL.real;
        const lobbyUrlToLoad = launchResult.launchURL.lobbyURL || null;

        setGameUrl(gameUrlToLoad);
        setLobbyUrl(lobbyUrlToLoad);
        setCurrentGameName(game.name);
        setIsGameIframeOpen(true);
        setIsGameLoading(false);

        log.debug('🎮 Lobby URL ricevuta:', lobbyUrlToLoad);
      } else {
        setIsGameLoading(false);
        showError('Errore', 'URL di gioco non disponibile');
      }

    } catch (error) {
      log.error('Errore avvio gioco:', error);
      setIsGameLoading(false);
      showError('Errore', 'Impossibile avviare il gioco. Riprova più tardi.');
    }
  }, [gameUser, findLiveGameById, updateUserToken, logout, createLoginParamsString, showWarning, showError]);

  const handleGameIframeClose = () => {
    setIsGameIframeOpen(false);
    setGameUrl(null);
    setCurrentGameName('');
    setLobbyUrl(null);
  };

  return {
    isGameLoading,
    isGameIframeOpen,
    gameUrl,
    currentGameName,
    lobbyUrl,
    handleGamePlay,
    handleGameIframeClose,
    createLoginParamsString
  };
}